import { useEffect, useState } from "react";
import type { MailDeliveryLog } from "@proof/accounts";
import { accountRequest, type AdministrationView } from "./account-api";
import { Select, Button, Control } from "./components";
type Page = {
  entries: MailDeliveryLog[];
  nextCursor?: string;
  retention: number;
};
export function EmailDeliveryLogs({
  refreshKey,
  connections,
}: {
  refreshKey: number;
  connections: AdministrationView["mail"];
}) {
  const [data, setData] = useState<Page>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [connection, setConnection] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [cursors, setCursors] = useState<string[]>([]);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    setCursors([]);
    setReload((n) => n + 1);
  }, [refreshKey]);
  const cursor = cursors.at(-1);
  useEffect(() => {
    let active = true;
    setBusy(true);
    setError("");
    const params = new URLSearchParams({
      ...(connection ? { connectionId: connection } : {}),
      ...(status ? { status } : {}),
      ...(query ? { search: query } : {}),
      ...(cursor ? { cursor } : {}),
    });
    accountRequest<Page>(`/email/logs?${params}`)
      .then((value) => {
        if (active) setData(value);
      })
      .catch((e) => {
        if (active) {
          setError(e.message);
          setData(undefined);
        }
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [connection, status, query, cursor, reload]);
  return (
    <section aria-label="Email delivery logs" id="email-delivery-logs">
      <h3>Email delivery logs</h3>
      <p>
        Provider responses for tests, invitations, verification codes, sign-in
        links and password resets. Accepted means the provider accepted the
        message; it does not confirm inbox delivery. Message bodies, passwords
        and access tokens are not retained.
      </p>
      <div className="control-grid">
        <Control label="Email log connection">
          <Select
            value={connection}
            onChange={(e) => {
              setConnection(e.target.value);
              setCursors([]);
            }}
          >
            <option value="">All managed connections</option>
            {connections.map((mail) => (
              <option key={mail.id} value={mail.id}>
                {mail.name}
              </option>
            ))}
          </Select>
        </Control>
        <Control label="Email log status">
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setCursors([]);
            }}
          >
            <option value="">All statuses</option>
            <option value="failed">Failed</option>
            <option value="accepted">Accepted by provider</option>
            <option value="pending">Pending / outcome unknown</option>
          </Select>
        </Control>
      </div>
      <form
        className="button-row"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(search.trim());
          setCursors([]);
        }}
      >
        <Control label="Search email logs">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Reference, recipient, error or provider code"
          />
        </Control>
        <Button type="submit">Search logs</Button>
        <Button
          disabled={busy}
          onClick={() => {
            setCursors([]);
            setReload((n) => n + 1);
          }}
        >
          Refresh delivery logs
        </Button>
      </form>
      {busy && <p role="status">Loading email delivery logs…</p>}
      {error && <p role="alert">{error}</p>}
      {!error && data && !data.entries.length && (
        <p>
          No matching delivery logs. Logs begin with this update; earlier
          provider responses were not retained. Send a new test to record its
          result.
        </p>
      )}
      {!!data?.entries.length && (
        <div className="account-table">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Connection / purpose</th>
                <th>Recipient</th>
                <th>Outcome</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((row) => (
                <tr key={row.id}>
                  <td>
                    {new Date(row.startedAt).toLocaleString()}
                    <small>
                      {row.durationMs === undefined
                        ? "In progress or outcome not recorded"
                        : `${row.durationMs} ms`}
                    </small>
                  </td>
                  <td>
                    {row.connectionName}
                    <small>
                      {row.provider} · {row.purpose}
                    </small>
                  </td>
                  <td>
                    {row.to}
                    <small>From: {row.from}</small>
                  </td>
                  <td>
                    {row.status === "accepted"
                      ? "Accepted by provider"
                      : row.status === "failed"
                        ? "Failed"
                        : "Pending / outcome unknown"}
                  </td>
                  <td>
                    <details>
                      <summary>View delivery details</summary>
                      <dl>
                        <dt>Reference</dt>
                        <dd>{row.id}</dd>
                        <dt>Stage</dt>
                        <dd>
                          {row.diagnostic?.stage ||
                            "Preparing provider request"}
                        </dd>
                        {row.diagnostic?.httpStatus && (
                          <>
                            <dt>HTTP status</dt>
                            <dd>{row.diagnostic.httpStatus}</dd>
                          </>
                        )}
                        {row.diagnostic?.smtpStatus && (
                          <>
                            <dt>SMTP status</dt>
                            <dd>{row.diagnostic.smtpStatus}</dd>
                          </>
                        )}
                        {row.diagnostic?.code && (
                          <>
                            <dt>Provider / network code</dt>
                            <dd>{row.diagnostic.code}</dd>
                          </>
                        )}
                        {row.diagnostic?.command && (
                          <>
                            <dt>SMTP command</dt>
                            <dd>{row.diagnostic.command}</dd>
                          </>
                        )}
                        {row.diagnostic?.providerMessageId && (
                          <>
                            <dt>Provider message ID</dt>
                            <dd>{row.diagnostic.providerMessageId}</dd>
                          </>
                        )}
                        <dt>Provider response</dt>
                        <dd
                          style={{
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {row.diagnostic?.message ||
                            "No final provider response was recorded. Check provider activity before sending again."}
                        </dd>
                      </dl>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && (
        <div className="button-row">
          <Button
            disabled={busy || !cursors.length}
            onClick={() => setCursors((rows) => rows.slice(0, -1))}
          >
            Newer logs
          </Button>
          <span>
            Page {cursors.length + 1} · up to {data.retention} recent attempts
            retained
          </span>
          <Button
            disabled={busy || !data.nextCursor}
            onClick={() => setCursors((rows) => [...rows, data.nextCursor!])}
          >
            Older logs
          </Button>
        </div>
      )}
    </section>
  );
}
